/**
 * SubscriptionStatus - User subscription tier status card
 *
 * MDM-UNIFIED-001 Phase 1: Tier Enforcement & Pricing
 *
 * Shows current tier, usage, and upgrade prompts.
 * Per constitution_ui.json §6: Real-time state feedback
 */
import React, { useState, useEffect } from 'react';
import {
  Crown,
  Zap,
  Package,
  Camera,
  ArrowRight,
  ExternalLink,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { API_BASE, getStoredToken } from '../../services/api';
import SubscriptionUpgradeModal from './SubscriptionUpgradeModal';

export default function SubscriptionStatus() {
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isManaging, setIsManaging] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = getStoredToken();
      const response = await fetch(`${API_BASE}/subscriptions/status`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscription status');
      }

      const data = await response.json();
      setStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setIsManaging(true);
    try {
      const token = getStoredToken();
      const response = await fetch(`${API_BASE}/subscriptions/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to access billing portal');
      }

      const data = await response.json();
      window.location.href = data.portal_url;
    } catch (err) {
      setError(err.message);
      setIsManaging(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
        <div className="flex items-center gap-3 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!status) return null;

  const tierConfig = {
    free: {
      label: 'Free',
      icon: Package,
      color: 'text-zinc-400',
      bgColor: 'bg-zinc-800',
      borderColor: 'border-zinc-700',
    },
    collector: {
      label: 'Collector',
      icon: Zap,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
    },
    intelligence: {
      label: 'Intelligence',
      icon: Crown,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
    },
  };

  const tier = tierConfig[status.tier] || tierConfig.free;
  const TierIcon = tier.icon;

  // Calculate usage percentages
  const gradingUsagePercent =
    status.grading_predictions_limit && status.grading_predictions_limit > 0
      ? Math.min(
          100,
          (status.grading_predictions_used / status.grading_predictions_limit) * 100
        )
      : 0;

  const collectionUsagePercent =
    status.collection_items_limit && status.collection_items_limit > 0
      ? Math.min(
          100,
          (status.collection_items_count / status.collection_items_limit) * 100
        )
      : 0;

  return (
    <>
      <div
        className={`bg-zinc-900 rounded-xl border ${tier.borderColor} p-6 space-y-6`}
      >
        {/* Tier header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${tier.bgColor}`}>
              <TierIcon className={`w-5 h-5 ${tier.color}`} />
            </div>
            <div>
              <h3 className="font-semibold text-white">{tier.label} Plan</h3>
              {status.current_period_end && (
                <p className="text-xs text-zinc-500">
                  Renews{' '}
                  {new Date(status.current_period_end).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>

          {status.is_paid ? (
            <button
              onClick={handleManageSubscription}
              disabled={isManaging}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-white transition-colors flex items-center gap-2"
            >
              {isManaging ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Manage
                  <ExternalLink className="w-4 h-4" />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg text-sm font-medium text-white transition-colors flex items-center gap-2"
            >
              Upgrade
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Usage meters */}
        <div className="space-y-4">
          {/* Grading predictions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Camera className="w-4 h-4 text-zinc-400" />
                <span className="text-sm text-zinc-300">AI Grading</span>
              </div>
              <span className="text-sm text-zinc-400">
                {status.grading_predictions_remaining === 'unlimited' ? (
                  <span className="text-green-400">Unlimited</span>
                ) : (
                  <>
                    {status.grading_predictions_used} /{' '}
                    {status.grading_predictions_limit}
                  </>
                )}
              </span>
            </div>
            {status.grading_predictions_limit && (
              <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    gradingUsagePercent >= 90
                      ? 'bg-red-500'
                      : gradingUsagePercent >= 70
                      ? 'bg-yellow-500'
                      : 'bg-orange-500'
                  }`}
                  style={{ width: `${gradingUsagePercent}%` }}
                />
              </div>
            )}
          </div>

          {/* Collection items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-zinc-400" />
                <span className="text-sm text-zinc-300">Collection Items</span>
              </div>
              <span className="text-sm text-zinc-400">
                {status.collection_items_remaining === 'unlimited' ? (
                  <span className="text-green-400">Unlimited</span>
                ) : (
                  <>
                    {status.collection_items_count} / {status.collection_items_limit}
                  </>
                )}
              </span>
            </div>
            {status.collection_items_limit && (
              <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    collectionUsagePercent >= 90
                      ? 'bg-red-500'
                      : collectionUsagePercent >= 70
                      ? 'bg-yellow-500'
                      : 'bg-orange-500'
                  }`}
                  style={{ width: `${collectionUsagePercent}%` }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Upgrade prompt for free users */}
        {!status.is_paid && (
          <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20 rounded-lg p-4">
            <p className="text-sm text-orange-300 mb-3">
              Upgrade to Collector for unlimited AI grading and 500 collection
              items.
            </p>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="text-sm text-orange-500 hover:text-orange-400 font-medium flex items-center gap-1"
            >
              View Plans
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Upgrade modal */}
      <SubscriptionUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={status.tier}
        onUpgradeSuccess={() => {
          setShowUpgradeModal(false);
          fetchStatus();
        }}
      />
    </>
  );
}
